import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Container, Button, Alert, Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { useSettings } from '../contexts/SettingsContext';
import { unsubscribeFromNewsletter, checkSubscriptionStatus } from '../services/newsletterService';
import SEO from '../components/SEO';

const UnsubscribePage = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { email: settingsEmail, updateSubscription } = useSettings();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [unsubscribed, setUnsubscribed] = useState(false);

  useEffect(() => {
    const emailParam = searchParams.get('email');
    if (emailParam) {
      const decodedEmail = decodeURIComponent(emailParam);
      setEmail(decodedEmail);
      checkStatus(decodedEmail);
    } else if (settingsEmail) {
      // If no email in URL but email exists in settings, use that
      setEmail(settingsEmail);
      checkStatus(settingsEmail);
    } else {
      setChecking(false);
      setMessage({ type: 'warning', text: t('newsletter.noEmailProvided') });
    }
  }, [searchParams, settingsEmail, t]);

  const checkStatus = async (emailToCheck) => {
    setChecking(true);
    try {
      const status = await checkSubscriptionStatus(emailToCheck);
      setIsSubscribed(status.subscribed);
      if (!status.exists) {
        setMessage({ type: 'info', text: t('newsletter.emailNotInList') });
      } else if (!status.subscribed) {
        setMessage({ type: 'info', text: t('newsletter.alreadyUnsubscribed') });
        setUnsubscribed(true);
      }
    } catch (error) {
      console.error('Error checking subscription status:', error);
      setMessage({ type: 'danger', text: t('newsletter.checkError') });
    } finally {
      setChecking(false);
    }
  };

  const handleUnsubscribe = async () => {
    if (!email) {
      setMessage({ type: 'danger', text: t('newsletter.noEmailProvided') });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const result = await unsubscribeFromNewsletter(email);
      if (result.success) {
        setMessage({ type: 'success', text: result.message || t('newsletter.unsubscribeSuccess') });
        setUnsubscribed(true);
        setIsSubscribed(false);
        
        // Sync with settings if the email matches
        if (settingsEmail && settingsEmail.toLowerCase() === email.toLowerCase()) {
          updateSubscription(false);
        }
      } else {
        setMessage({ type: 'danger', text: result.message || t('newsletter.unsubscribeError') });
      }
    } catch (error) {
      console.error('Unsubscribe error:', error);
      setMessage({ type: 'danger', text: t('newsletter.unsubscribeError') });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title={t('newsletter.unsubscribeTitle')}
        description={t('newsletter.unsubscribeDescription')}
      />
      <div style={{
        minHeight: 'calc(100vh - 120px)',
        backgroundColor: 'var(--bg-color)',
        color: 'var(--text-color)',
        transition: 'background-color 0.3s ease, color 0.3s ease',
        padding: '40px 0'
      }}>
        <Container style={{ maxWidth: '600px' }}>
          <Card style={{
            backgroundColor: 'var(--card-bg)',
            borderColor: 'var(--border-color)',
            color: 'var(--text-color)'
          }}>
            <Card.Body className="p-4">
              <h2 className="mb-4">{t('newsletter.unsubscribeTitle')}</h2>
              
              {checking ? (
                <Alert variant="info">
                  {t('common.loading')}
                </Alert>
              ) : (
                <>
                  {email && (
                    <p className="mb-3">
                      <strong>{t('newsletter.emailLabel')}:</strong> {email}
                    </p>
                  )}

                  {message.text && (
                    <Alert variant={message.type} className="mb-3">
                      {message.text}
                    </Alert>
                  )}

                  {!unsubscribed && isSubscribed && (
                    <div className="mb-3">
                      <p>{t('newsletter.confirmUnsubscribe')}</p>
                      <Button
                        variant="danger"
                        onClick={handleUnsubscribe}
                        disabled={loading}
                        size="lg"
                        className="w-100"
                      >
                        {loading ? t('newsletter.unsubscribing') : t('newsletter.confirmUnsubscribeButton')}
                      </Button>
                    </div>
                  )}

                  {unsubscribed && (
                    <div className="text-center">
                      <Alert variant="success" className="mb-3">
                        {t('newsletter.unsubscribeSuccess')}
                      </Alert>
                      <Button
                        variant="primary"
                        onClick={() => navigate('/')}
                      >
                        {t('newsletter.returnHome')}
                      </Button>
                    </div>
                  )}

                  {!email && (
                    <Alert variant="warning">
                      {t('newsletter.noEmailProvided')}
                    </Alert>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Container>
      </div>
    </>
  );
};

export default UnsubscribePage;
